import { useRef, useEffect, useCallback, useState } from 'react';

const MAX_HEIGHT = 160;
const CHAR_COUNTER_THRESHOLD = 500;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
const FILE_TYPES = ".txt,.csv,.md,.html,.htm,.pdf,.docx,.doc,.pptx,.xlsx,.json,.xml,.py,.tsx,.js,.svg,.png,.jpg,.jpeg,.webp,.wav,.mp3,.mp4"

export default function ChatInput({ value, onChange, onSend, loading, inputRef: externalRef }) {
  const internalRef = useRef(null);
  const textareaRef = externalRef || internalRef;
  const fileRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, MAX_HEIGHT) + 'px';
  }, [value, textareaRef]);

  useEffect(() => { textareaRef.current?.focus(); }, [textareaRef]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend(pendingFile);
      setPendingFile(null);
    }
  }, [onSend, pendingFile]);

  const handleClear = useCallback(() => {
    onChange('');
    textareaRef.current?.focus();
  }, [onChange, textareaRef]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) { alert('File is too large (max 10 MB).'); return; }
    setPendingFile(file);
    e.target.value = '';
  };

  const handleRemoveFile = () => setPendingFile(null);

  const showCharCounter = value.length > CHAR_COUNTER_THRESHOLD;
  const showClearBtn    = value.length > 0 && !loading;

  return (
    <div className="chat-input-bar">
      {/* File chip — shown when a file is attached */}
      {pendingFile && (
        <div className="file-chip">
          <span>⎿ {pendingFile.name}</span>
          <button onClick={handleRemoveFile} aria-label="Remove file">×</button>
        </div>
      )}

      <div className="chat-input-inner">
        {/* Hidden file input */}
        <input
          ref={fileRef}
          type="file"
          accept={FILE_TYPES}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {/* ❯ prompt prefix — terminal style */}

        {/* Attach button */}
        <button
          className="attach-btn"
          onClick={() => fileRef.current?.click()}
          disabled={loading}
          aria-label="Attach file"
          title="Attach file"
        >
          +
        </button>

        <span className="input-prompt-char" aria-hidden="true">❯</span>

        <textarea
          id={textareaRef}
          ref={textareaRef}
          className="chat-textarea"
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="type a message…"
          rows={1}
          readOnly={loading}
          autoFocus
          aria-label="Message input"
          aria-describedby="input-hint"
        />

        {showClearBtn && (
          <button
            className="clear-btn"
            onClick={handleClear}
            aria-label="Clear message"
            tabIndex={0}
            title="Clear"
          >
            ✕
          </button>
        )}

        <button
          onClick={() => {
            onSend(pendingFile);
            setPendingFile(null);
          }}
          disabled={loading || (!value.trim() && !pendingFile)}
          className="send-btn"
          aria-label="Send message"
        >
          {loading ? '…' : '⏎'}
        </button>
      </div>

      <div className="input-footer">
        <p id="input-hint" className="input-hint">
          Enter ↵ send · Shift+Enter new line
        </p>
        {showCharCounter && (
          <span className={`char-counter ${value.length > 2000 ? 'char-counter--warn' : ''}`}>
            {value.length.toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}
